export interface BrandTokens {
  fontFamily: string;
  primary: { main: string; dark: string; light: string; bg: string };
  secondary: { main: string; dark: string; light: string };
  info: { main: string; bg: string };
  text: { primary: string; secondary: string; disabled: string; muted: string };
  background: { page: string; paper: string; platform: string; muted: string; icon: string };
  border: { default: string; strong: string; light: string; primary: string; dashed: string };
  gradients: {
    primary: string;
    primaryHover: string;
    progress: string;
    subtle: string;
    unlock: string;
  };
}

/** Every group optional, every key inside a group optional. */
export type BrandOverrides = {
  [K in keyof BrandTokens]?: BrandTokens[K] extends string ? string : Partial<BrandTokens[K]>;
};

export type KnowledgeDocIcon = 'taxonomy' | 'kpi';

export interface KnowledgeDoc {
  title: string;
  subtitle: string;
  icon: KnowledgeDocIcon;
  /** Served from the SPA's public/ folder, opened in the Knowledge dialog iframe. */
  href: string;
}

export type SendGridClient = 'alison' | 'incubeta';

export interface TenantEmail {
  supportEmail: string;
  fromEmail: string;
  fromName: string;
  /** Selects the API key and template set in apps/trpc/actions/src/mail. */
  sendgridClient: SendGridClient;
}

export interface ClientConfig {
  /** Internal name, also the SendGrid client discriminator. */
  name: string;
  /** Hostnames (with or without port) that select this config. */
  hosts: string[];
  /** Wordmark URL. Undefined = Alison SVG. */
  logo?: string;
  /** Glyph URL: favicon, sidebar mark, in-app small icon. */
  icon?: string;
  /** Tenant stylesheet path under public/, loaded before first paint (fonts, CSS vars). */
  stylesheet?: string;
  brand?: BrandOverrides;
  /** Replaces the word "Alison" in UI copy and the document title. */
  providerName?: string;
  /** "<picksLabel> Picks" heading. */
  picksLabel?: string;
  /** Export credits footer. */
  websiteUrl?: string;
  /** Sidebar destinations to hide, by SidebarDestination id (string to stay React-free here). */
  hiddenNav?: string[];
  /** When set, the Knowledge item opens these instead of docs.app.alison.ai. */
  knowledgeDocs?: KnowledgeDoc[];
  email?: Partial<TenantEmail>;
}

export const ALISON_DEFAULTS = {
  providerName: 'Alison',
  picksLabel: 'Alison',
  websiteUrl: 'https://alison.ai/',
  logo: '/alison-wordmark.svg',
  icon: '/alison-favicon.svg',
  email: {
    supportEmail: 'support@alison.ai',
    fromEmail: 'no-reply@alison.ai',
    fromName: 'Alison',
    sendgridClient: 'alison',
  } satisfies TenantEmail,
} as const;

/** ClientConfig with every optional filled from ALISON_DEFAULTS. What components consume. */
export interface ResolvedTenant {
  name: string;
  isAlison: boolean;
  logo: string;
  icon: string;
  stylesheet?: string;
  brand?: BrandOverrides;
  providerName: string;
  picksLabel: string;
  websiteUrl: string;
  hiddenNav: string[];
  knowledgeDocs: KnowledgeDoc[];
  email: TenantEmail;
}

export function resolveTenant(config?: ClientConfig): ResolvedTenant {
  const d = ALISON_DEFAULTS;
  const resolved: ResolvedTenant = {
    name: config?.name ?? 'Alison',
    isAlison: config === undefined,
    logo: config?.logo ?? d.logo,
    icon: config?.icon ?? d.icon,
    providerName: config?.providerName ?? d.providerName,
    picksLabel: config?.picksLabel ?? d.picksLabel,
    websiteUrl: config?.websiteUrl ?? d.websiteUrl,
    hiddenNav: config?.hiddenNav ?? [],
    knowledgeDocs: config?.knowledgeDocs ?? [],
    email: { ...d.email, ...config?.email },
  };
  if (config?.stylesheet) resolved.stylesheet = config.stylesheet;
  if (config?.brand) resolved.brand = config.brand;
  return resolved;
}
